import { COLLISION_GROUPS } from '../../../constants/Constants';
import { GameEvents } from '../../GameEvents';

export class CharacterAttack extends Phaser.Events.EventEmitter {
  constructor(scene, character, config) {
    super();
    this._scene = scene;
    this._character = character;
    this._config = config;
    this._cooldown = config.cooldown;
    this._active = false;
    this._complete = false;
    this._earnSpecialPoints = config.earnSpecialPoints;
    this._costSpecialPoints = config.specialPoints;
    this._targetsHit = [];
    this._targetHitLast = false;
  }

  get name() {
    return this._config.name || this._config.animation;
  }

  get scene() {
    return this._scene;
  }

  get active() {
    return this._active;
  }

  get scene() {
    return this._scene;
  }

  get damage() {
    return this._config.damage;
  }

  get type() {
    return this._config.type;
  }

  get knockdown() {
    return this._config.knockdown;
  }

  get stagger() {
    return this._config.stagger;
  }

  get character() {
    return this._character;
  }

  get canTrigger() {
    return !this.active && this.cooldown <= 0;
  }

  get cooldown() {
    return this._cooldown;
  }

  get attackCollider() {
    return this.character.attackCollider;
  }

  get complete() {
    return this._complete;
  }

  trigger() {
    this._active = true;
    this._targetsHit = [];
    this._complete = false;
    this._targetHitLast = false;
    this._character.attackCollider.enabled = true;
    if (this._costSpecialPoints) this._character.adjustSpecialPoints(-this._costSpecialPoints);
    this.disableBodyCollider();
    this.updateAttackCollider();
    // trigger animations and whatever is needed
    this._triggerAttackAnimation();
  }

  _triggerAttackAnimation() {
    this.character.playAnimation(this._config.animation, false, this.completeAttack, this);
  }

  update(delta) {
    // run any frame update actions
    this._active ? this.updateActive(delta) : this.updateInactive(delta);
  }

  updateActive(delta) {
    this.updateAttackCollider();
    this.checkAttackTargets(delta);
  }

  updateAttackCollider() {
    const bounds = this._character.getBounds();
    this._character.attackCollider.x = bounds.x;
    this._character.attackCollider.y = bounds.y;
    this._character.attackCollider.width = bounds.width;
    this._character.attackCollider.height = bounds.height;
  }

  checkAttackTargets(delta) {
    const collisions = this._character.attackCollider.getCollisions();
    let damaged = false;
    for (let c, i = 0; i < collisions.length; i++) {
      c = collisions[i];
      if (c.gameObject.canDamage && !this._targetsHit.includes(c)) {
        this._targetsHit.push(c);
        this.damageTarget(c.gameObject);
        damaged = true;
      }
    }
    if (damaged) {
      this.scene.events.emit(GameEvents.PLAYER_ATTACK_HIT, {
        x:
          this.attackCollider.x +
          (this._character.faceDirection > 0 ? this.attackCollider.width : 0),
        y: this.attackCollider.y + this.attackCollider.height / 2,
        dir: this._character.faceDirection,
      });
      if (this._character.hasWeapon) {
        this.playSFX(this._character.weapon.damagesfx || 'hit_impact');
        this._character.damageWeapon();
      } else {
        this.playSFX('hit_impact');
      }
    }
  }

  damageTarget(target) {
    if (this._earnSpecialPoints) {
      this._character.adjustSpecialPoints(this.damage);
    }
    target.damage(this._character.godMode ? target.health : this.damage, {
      stagger: this.stagger,
      knockdown: this.knockdown,
    });
    if (this.character._type === 'hero')
      this.character.scene.events.emit(GameEvents.DEAL_DAMAGE, { target, damage: this.damage });
    if (
      this.character.setTargetHitLast &&
      target.bodyCollider &&
      target.bodyCollider.collisionGroup === COLLISION_GROUPS.ENEMY_BODY
    ) {
      this._targetHitLast = target;
      this.character.setTargetHitLast(target);
    }
  }

  updateInactive(delta) {
    if (this._cooldown > 0) this._cooldown = Math.max(0, this._cooldown - delta);
  }

  cancel() {
    this._active = false;
    this._cooldown = this._config.cooldown;
  }

  completeAttack() {
    this._active = false;
    this._complete = true;
    this._cooldown = this._config.cooldown;
    this.disableAttackCollider();
    this.enableBodyCollider();
  }

  disableAttackCollider() {
    this._character.attackCollider.enabled = false;
  }

  enableAttackCollider() {
    this._character.attackCollider.enabled = true;
  }

  disableBodyCollider() {
    this._character.bodyCollider.enabled = false;
  }

  enableBodyCollider() {
    this._character.bodyCollider.enabled = true;
  }

  // collidersOverlap(colliderA, colliderB) {
  //   return (
  //     colliderA.x < colliderB.x + colliderB.width &&
  //     colliderA.x + colliderA.width > colliderB.x &&
  //     colliderA.y < colliderB.y + colliderB.height &&
  //     colliderA.height + colliderA.y > colliderB.y
  //   );
  // }

  playSFX(id, sprite) {
    this.character.scene.game.soundController.playSFX(sprite || 'sfx', id);
  }
}

CharacterAttack.Events = {
  COMPLETE: 'COMPLETE',
};

// =============================================================================================
// FAKE ATTACK
// =============================================================================================
export class CharacterAttackFaked extends CharacterAttack {
  get attackCollider() {
    const result = new Phaser.Geom.Rectangle(
      this.character.x + this._fakeAttackDisplay.x,
      this.character.y + this._fakeAttackDisplay.y,
      this._fakeAttackDisplay.displayWidth,
      this._fakeAttackDisplay.displayHeight
    );
    return result;
  }

  trigger() {
    this._active = true;
    this._targetsHit = [];
    this._complete = false;
    this._fakeAttackDisplay = this.scene.add.rectangle(0, 0, 1, 200, 0xfff0000, 0.25).setOrigin(0);
    this._fakeAttackDisplay.y = -this._fakeAttackDisplay.displayHeight;
    this.character.add(this._fakeAttackDisplay);
    this.scene.tweens.add({
      targets: this._fakeAttackDisplay,
      x: this.character.faceDirection > 0 ? 0 : -this.character.attackRange,
      width: this.character.attackRange,
      duration: 300,
      yoyo: true,
      onComplete: this.completeAttack,
      onCompleteScope: this,
    });
  }

  completeAttack() {
    super.completeAttack();
    if (this._fakeAttackDisplay) {
      this._fakeAttackDisplay.destroy();
      this._fakeAttackDisplay = null;
    }
  }
}
